import { Modal } from '@/components/admin/Modal';
import { Button } from '@/components/ui/button';
import { ApiError, api } from '@/lib/api';
import { formatElapsed } from '@/lib/formatElapsed';
import type { AdminFileUploadListItem } from '@innermate/shared';
import type { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

export function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function previewKind(mime: string): 'image' | 'pdf' | 'text' | 'none' {
  const m = mime.toLowerCase();
  if (m.startsWith('image/')) return 'image';
  if (m === 'application/pdf' || m.endsWith('/pdf')) return 'pdf';
  if (m.startsWith('text/') || m === 'application/json') return 'text';
  return 'none';
}

export function OrphanBadge({ item }: { item: AdminFileUploadListItem }) {
  if (item.sessionCount > 0) {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-emerald-500/10 px-2 py-0.5 text-[11px] text-emerald-700 dark:text-emerald-400">
        <span className="size-1.5 rounded-full bg-emerald-500" />
        대화 {item.sessionCount}건
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded bg-destructive/10 px-2 py-0.5 text-[11px] text-destructive">
      <span className="size-1.5 rounded-full bg-destructive" />
      고아 (대화 없음)
    </span>
  );
}

export function StatusBadge({ status, error }: { status: string; error: string | null }) {
  const tone: Record<string, string> = {
    uploading: 'bg-surface-muted text-foreground-muted',
    uploaded: 'bg-surface-muted text-foreground-muted',
    queued: 'bg-surface-muted text-foreground-muted',
    processing: 'bg-sky-500/10 text-sky-700 dark:text-sky-400',
    ready: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
    failed: 'bg-destructive/10 text-destructive',
    canceled: 'bg-destructive/10 text-destructive',
  };
  const klass = tone[status] ?? 'bg-surface-muted';
  return (
    <div>
      <span className={`inline-block rounded px-2 py-0.5 text-[11px] font-mono uppercase ${klass}`}>
        {status}
      </span>
      {error ? (
        <div className="mt-1 text-[10px] text-destructive line-clamp-2" title={error}>
          {error}
        </div>
      ) : null}
    </div>
  );
}

function MetaRow({ k, children }: { k: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[7rem_1fr] gap-3 text-sm">
      <dt className="text-foreground-muted">{k}</dt>
      <dd className="break-all">{children}</dd>
    </div>
  );
}

function PreviewArea({
  kind,
  contentUrl,
  mime,
}: {
  kind: 'image' | 'pdf' | 'text' | 'none';
  contentUrl: string;
  mime: string;
}) {
  if (kind === 'image') {
    return (
      <img
        src={contentUrl}
        alt=""
        className="max-h-96 max-w-full rounded border border-border object-contain bg-surface-muted/40"
      />
    );
  }
  if (kind === 'pdf') {
    return (
      <iframe
        title="pdf preview"
        src={contentUrl}
        className="h-96 w-full rounded border border-border bg-white"
      />
    );
  }
  if (kind === 'text') {
    return (
      <iframe
        title="text preview"
        src={contentUrl}
        className="h-64 w-full rounded border border-border bg-surface-muted/40"
      />
    );
  }
  return (
    <div className="text-xs text-foreground-muted">
      이 형식({mime || '알 수 없음'})은 브라우저 미리보기를 지원하지 않습니다. 다운로드해서
      확인하세요.
    </div>
  );
}

/**
 * 삭제 mutation 시그니처 — 호출자(페이지)가 자체 invalidation 컨텍스트로 mutation 을 만들고
 * 모달에 주입한다. ChatLog 처럼 모달만 띄우는 페이지는 mutation 의 onSuccess 에서 trace 재조회
 * 같은 다른 후처리를 자유롭게 연결.
 */
export type FileUploadDeleteMut = ReturnType<
  typeof useMutation<
    Awaited<ReturnType<typeof api.adminDeleteFileUpload>>,
    unknown,
    { id: string; confirmActive: boolean }
  >
>;

/**
 * 첨부파일 상세 모달 — FileUploads 페이지의 메인 사용처이지만 ChatLog 등 외부에서도 동일하게
 * 띄울 수 있도록 추출. 부모는 item / onClose / onDeleted / deleteMut 만 주입.
 */
export function FileUploadDetailModal({
  item,
  onClose,
  onDeleted,
  deleteMut,
}: {
  item: AdminFileUploadListItem;
  onClose: () => void;
  onDeleted: () => void;
  deleteMut: FileUploadDeleteMut;
}) {
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState<
    null | { kind: 'orphan' } | { kind: 'active'; sessionCount: number; linkedQueryCount: number }
  >(null);
  const [error, setError] = useState<string | null>(null);
  const pKind = previewKind(item.mime);
  const isOrphan = item.sessionCount === 0 && item.linkedQueryCount === 0;
  const contentUrl = api.adminFileUploadContentUrl(item.id, true);
  const downloadUrl = api.adminFileUploadContentUrl(item.id, false);

  async function handleDeleteClick() {
    setError(null);
    if (isOrphan) {
      setConfirmOpen({ kind: 'orphan' });
      return;
    }
    try {
      const res = await deleteMut.mutateAsync({ id: item.id, confirmActive: false });
      if (res.ok) {
        onDeleted();
      } else {
        setConfirmOpen({
          kind: 'active',
          sessionCount: res.sessionCount,
          linkedQueryCount: res.linkedQueryCount,
        });
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '삭제에 실패했습니다.');
    }
  }

  async function confirmDelete(confirmActive: boolean) {
    setError(null);
    try {
      const res = await deleteMut.mutateAsync({ id: item.id, confirmActive });
      if (res.ok) {
        setConfirmOpen(null);
        onDeleted();
      } else {
        setError('세션 상태가 바뀌었습니다. 다시 시도해주세요.');
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '삭제에 실패했습니다.');
    }
  }

  return (
    <>
      <Modal
        open
        onClose={onClose}
        title={item.originalName}
        description={`${item.mime} · ${formatBytes(item.sizeBytes)}`}
        size="lg"
        footer={
          <div className="flex items-center justify-between gap-2">
            <div className="flex gap-2">
              <a
                href={downloadUrl}
                download={item.originalName}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-surface-muted px-4 text-sm font-medium text-foreground hover:bg-surface-muted/80 border border-border"
              >
                다운로드
              </a>
              {item.kind === 'excel' && item.linkedQueryCount > 0 ? (
                <Button
                  variant="secondary"
                  onClick={() =>
                    navigate({
                      to: '/admin/saved-queries',
                      // URL params 로 filter 전달은 현재 지원 안 함 — 단순 이동 후 관리자가 필터.
                    })
                  }
                >
                  연관 쿼리 {item.linkedQueryCount}건 보기
                </Button>
              ) : null}
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={onClose}>
                닫기
              </Button>
              <Button variant="danger" onClick={handleDeleteClick} disabled={deleteMut.isPending}>
                {deleteMut.isPending ? '삭제 중…' : '삭제'}
              </Button>
            </div>
          </div>
        }
      >
        <div className="space-y-3">
          <MetaRow k="소유자">
            {item.ownerDisplayName ?? '—'}{' '}
            <span className="text-foreground-muted">· {item.ownerEmail ?? ''}</span>
          </MetaRow>
          <MetaRow k="업로드">
            {formatDateTime(item.createdAt)}
            {item.uploadMs != null ? (
              <span className="text-foreground-muted"> · 소요 {formatElapsed(item.uploadMs)}</span>
            ) : null}
          </MetaRow>
          <MetaRow k="준비">
            {item.readyAt ? formatDateTime(item.readyAt) : '미처리'}
            {item.status === 'ready' && item.readyAt ? (
              <span className="text-foreground-muted">
                {' '}
                · 분석{' '}
                {formatElapsed(
                  new Date(item.readyAt).getTime() - new Date(item.createdAt).getTime(),
                )}
              </span>
            ) : null}
          </MetaRow>
          <MetaRow k="상태">
            <StatusBadge status={item.status} error={item.error} />
          </MetaRow>
          <MetaRow k="대화 연결">
            <OrphanBadge item={item} />
            {item.sessionSamples.length > 0 ? (
              <ul className="mt-2 space-y-1 text-xs">
                {item.sessionSamples.map((s) => (
                  <li key={s.id}>
                    <button
                      type="button"
                      className="text-left hover:underline"
                      onClick={() =>
                        navigate({
                          to: '/admin/chat-log',
                          search: {
                            sessionId: s.id,
                            // 파일 업로드일을 기준으로 -30일 ~ 업로드일 범위에서 세션 찾기.
                            // session.createdAt ≤ file.createdAt 이므로 to 는 파일 날짜.
                            date: item.createdAt.slice(0, 10),
                          },
                        })
                      }
                    >
                      {s.title ?? '(제목 없음)'}{' '}
                      <span className="text-foreground-muted font-mono">· {s.id.slice(0, 8)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </MetaRow>
          {item.kind === 'excel' ? (
            <MetaRow k="연결 쿼리">
              {item.linkedQueryCount > 0
                ? `${item.linkedQueryCount}건의 저장된 쿼리가 이 엑셀을 참조합니다.`
                : '없음'}
            </MetaRow>
          ) : null}
          {item.status === 'ready' || item.kind === 'other' ? (
            <div className="pt-2">
              <div className="text-xs text-foreground-muted mb-1.5">미리보기</div>
              <PreviewArea kind={pKind} contentUrl={contentUrl} mime={item.mime} />
            </div>
          ) : null}
          {error ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          ) : null}
        </div>
      </Modal>

      {confirmOpen?.kind === 'orphan' ? (
        <Modal
          open
          layer="high"
          onClose={() => setConfirmOpen(null)}
          title="고아 파일 삭제"
          size="sm"
          footer={
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setConfirmOpen(null)}>
                취소
              </Button>
              <Button
                variant="danger"
                onClick={() => confirmDelete(false)}
                disabled={deleteMut.isPending}
              >
                {deleteMut.isPending ? '삭제 중…' : '삭제'}
              </Button>
            </div>
          }
        >
          <p className="text-sm">
            이 파일은 어떤 대화에도 연결되어 있지 않고, 저장된 쿼리도 참조하지 않습니다. 스토리지 +
            DB 에서 완전히 제거됩니다.
          </p>
        </Modal>
      ) : null}

      {confirmOpen?.kind === 'active' ? (
        <Modal
          open
          layer="high"
          onClose={() => setConfirmOpen(null)}
          title="사용 중인 대화/쿼리가 있습니다"
          description={`대화 ${confirmOpen.sessionCount}건 · 연결 쿼리 ${confirmOpen.linkedQueryCount}건`}
          size="sm"
          footer={
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setConfirmOpen(null)}>
                취소
              </Button>
              <Button
                variant="danger"
                onClick={() => confirmDelete(true)}
                disabled={deleteMut.isPending}
              >
                {deleteMut.isPending ? '삭제 중…' : '알면서도 삭제'}
              </Button>
            </div>
          }
        >
          <p className="text-sm">
            이 파일이 아직 살아있는 대화에 첨부되어 있거나 저장된 쿼리가 참조하고 있습니다. 삭제하면
            해당 쿼리는 즉시 만료되고, 대화에서는 더 이상 첨부파일을 열람할 수 없습니다.
          </p>
          <p className="mt-2 text-sm">정말 삭제하시겠습니까?</p>
        </Modal>
      ) : null}
    </>
  );
}
